/** Configuración global (superadmin): tasas (manual + dolarapi) y usuarios. */
import { ROLES, usuarioCrearInput, type SucursalDTO, type UsuarioCrearInput } from '@mg/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { BotonActivo } from '@/componentes/BotonActivo.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError, patch, post } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { ETIQUETA_ROL, formatearFechaHora } from '@/lib/utils.ts';
import { CatalogoCard } from './Catalogo.tsx';
import { EstructuraCard, ParametrosCard } from './ConfiguracionAvanzada.tsx';
import { EmpresaCard } from './Empresa.tsx';
import { FirmaRetencionesCard } from './FirmaRetenciones.tsx';
import { TasasCard } from './Tasas.tsx';
import { DialogoPermisosUsuario } from './PermisosUsuario.tsx';

interface UsuarioDTO {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  sucursal_nombre: string | null;
  activo: boolean;
  ultimo_login: string | null;
}

export function Configuracion() {
  const esDuena = useAuth().usuario?.rol === 'superadmin';
  const qc = useQueryClient();
  const usuarios = useQuery({ queryKey: claves.usuarios, queryFn: () => get<UsuarioDTO[]>('/usuarios') });
  const sucursales = useQuery({ queryKey: claves.sucursales, queryFn: () => get<SucursalDTO[]>('/sucursales') });
  const [nuevoUsuario, setNuevoUsuario] = useState(false);
  const [permisosDe, setPermisosDe] = useState<string | null>(null);
  const [claveDe, setClaveDe] = useState<UsuarioDTO | null>(null);
  const [claveNueva, setClaveNueva] = useState('');

  const formUsuario = useForm<UsuarioCrearInput>({ resolver: zodResolver(usuarioCrearInput), defaultValues: { email: '', nombre: '', password: '', rol: 'cajero', sucursal_id: null } });
  const crearUsuario = useMutation({
    mutationFn: (d: UsuarioCrearInput) => post<UsuarioDTO>('/usuarios', d),
    onSuccess: async (u) => {
      toast.success(`Usuario ${u.email} creado`);
      setNuevoUsuario(false);
      formUsuario.reset();
      await qc.invalidateQueries({ queryKey: claves.usuarios });
    },
    onError: (e) => toast.error('No se pudo crear', { description: mensajeDeError(e) }),
  });
  const cambiarClave = useMutation({
    mutationFn: (u: UsuarioDTO) => patch<UsuarioDTO>(`/usuarios/${u.id}`, { password: claveNueva }),
    onSuccess: (u) => {
      toast.success(`Clave de ${u.nombre} cambiada`, { description: 'Sus sesiones abiertas se cerraron: entra con la clave nueva. Queda en auditoría.' });
      setClaveDe(null);
      setClaveNueva('');
    },
    onError: (e) => toast.error('No se pudo cambiar la clave', { description: mensajeDeError(e) }),
  });
  const alternarActivo = useMutation({
    mutationFn: ({ u, activo }: { u: UsuarioDTO; activo: boolean }) => patch<UsuarioDTO>(`/usuarios/${u.id}`, { activo }),
    onSuccess: async (u) => {
      toast.success(u.activo ? `${u.nombre} activado` : `${u.nombre} desactivado`);
      await qc.invalidateQueries({ queryKey: claves.usuarios });
    },
    onError: (e) => toast.error('No se pudo cambiar el estado', { description: mensajeDeError(e) }),
  });

  return (
    <>
      <TituloPagina titulo="Configuración global" subtitulo="Tasas, usuarios y parámetros del sistema. Exclusivo del superadmin." />
      <div className="grid gap-4 xl:grid-cols-2">
        <TasasCard />

        <Card>
          <CardHeader className="flex-row items-start justify-between">
            <div>
              <div className="flex items-center gap-1"><CardTitle>Usuarios</CardTitle><AyudaVentana tema="configuracion.usuarios" /></div>
              <CardDescription>Desactivar, nunca borrar. Cada usuario pertenece a UNA sucursal (salvo superadmin y contador). Con «Permisos» le da o le quita funciones a cada uno sobre la plantilla de su rol.</CardDescription>
            </div>
            <Button size="sm" onClick={() => setNuevoUsuario(true)} data-testid="btn-nuevo-usuario">
              <UserPlus /> Nuevo
            </Button>
          </CardHeader>
          <CardContent>
            <EstadoQuery cargando={usuarios.isLoading} error={usuarios.error} datos={usuarios.data} reintentar={() => void usuarios.refetch()}>
              {(lista) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Sucursal</TableHead>
                      <TableHead>Último acceso</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lista.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <p className="font-medium">{u.nombre}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </TableCell>
                        <TableCell>{ETIQUETA_ROL[u.rol]}</TableCell>
                        <TableCell>{u.sucursal_nombre ?? <span className="text-muted-foreground">Global</span>}</TableCell>
                        <TableCell>{u.ultimo_login ? formatearFechaHora(u.ultimo_login) : '—'}</TableCell>
                        <TableCell>
                          <Badge variant={u.activo ? 'ok' : 'critico'}>{u.activo ? 'Activo' : 'Inactivo'}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setPermisosDe(u.id)} data-testid={`usuario-permisos-${u.email}`}>Permisos</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setClaveNueva(''); setClaveDe(u); }} data-testid={`usuario-clave-${u.email}`}>Clave</Button>
                          <BotonActivo
                            activo={u.activo}
                            titulo={`Desactivar a ${u.nombre}`}
                            consecuencia="Su acceso se corta al instante: no puede entrar al sistema ni seguir trabajando en la sesión que tenga abierta. Lo que hizo queda igual en sus documentos y en la auditoría."
                            disabled={alternarActivo.isPending}
                            alCambiar={(activo) => alternarActivo.mutateAsync({ u, activo })}
                            testid={`usuario-alternar-${u.email}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </EstadoQuery>
          </CardContent>
        </Card>
        {/* Lo que vale para toda la empresa lo cambia solo la dueña (decisión del 2026-10-27, D10): quien tiene el permiso
            de configuración delegado ve la estructura de su sucursal, las categorías y los usuarios */}
        {esDuena ? <ParametrosCard /> : null}
        {esDuena ? <EmpresaCard /> : null}
        <EstructuraCard />
        <CatalogoCard listasEditables={esDuena} />
        {esDuena ? <FirmaRetencionesCard /> : null}
        {esDuena ? null : <p className="rounded-lg border bg-muted/50 px-3 py-2 text-sm text-muted-foreground" data-testid="config-solo-duena">Los parámetros (IVA, IGTF, topes), los datos fiscales de la empresa, la firma de los comprobantes y las listas de precio valen para toda la empresa: los cambia solo la dueña.</p>}
      </div>

      <DialogoPermisosUsuario usuarioId={permisosDe} cerrar={() => setPermisosDe(null)} />
      <Dialog open={claveDe !== null} onOpenChange={(o) => { if (!o) setClaveDe(null); }}>
        <DialogContent data-testid="dialogo-clave">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Cambiar la clave de {claveDe?.nombre}</DialogTitle><AyudaVentana tema="configuracion.usuarios" /></div>
            <DialogDescription>Para cuando la olvidó o hay que quitársela a otra persona. Se cierran sus sesiones abiertas y, si estaba bloqueado por intentos fallidos, se desbloquea.</DialogDescription>
          </DialogHeader>
          <Campo etiqueta="Clave nueva" htmlFor="u-clave-nueva" ayuda="Mínimo 8 caracteres. Dísela en persona.">
            <Input id="u-clave-nueva" type="password" autoComplete="new-password" value={claveNueva} onChange={(e) => setClaveNueva(e.target.value)} data-testid="u-clave-nueva" />
          </Campo>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaveDe(null)}>Cancelar</Button>
            <Button disabled={claveNueva.length < 8} cargando={cambiarClave.isPending} onClick={() => claveDe && cambiarClave.mutate(claveDe)} data-testid="u-clave-guardar">Cambiar clave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={nuevoUsuario} onOpenChange={setNuevoUsuario}>
        <DialogContent>
          <form onSubmit={formUsuario.handleSubmit((d) => crearUsuario.mutate({ ...d, sucursal_id: d.sucursal_id || null }))} noValidate className="space-y-4">
            <DialogHeader>
              <div className="flex items-center gap-1"><DialogTitle>Nuevo usuario</DialogTitle><AyudaVentana tema="configuracion.usuarios" /></div>
              <DialogDescription>El rol y la sucursal definen lo que verá al entrar.</DialogDescription>
            </DialogHeader>
            <Campo etiqueta="Nombre" htmlFor="u-nombre" error={formUsuario.formState.errors.nombre?.message}>
              <Input id="u-nombre" {...formUsuario.register('nombre')} />
            </Campo>
            <Campo etiqueta="Correo" htmlFor="u-email" error={formUsuario.formState.errors.email?.message}>
              <Input id="u-email" type="email" {...formUsuario.register('email')} />
            </Campo>
            <Campo etiqueta="Contraseña inicial" htmlFor="u-pass" error={formUsuario.formState.errors.password?.message} ayuda="Mínimo 8 caracteres">
              <Input id="u-pass" type="password" {...formUsuario.register('password')} />
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo etiqueta="Rol" htmlFor="u-rol" error={formUsuario.formState.errors.rol?.message}>
                <Select id="u-rol" {...formUsuario.register('rol')}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ETIQUETA_ROL[r]}
                    </option>
                  ))}
                </Select>
              </Campo>
              <Campo etiqueta="Sucursal" htmlFor="u-suc" error={formUsuario.formState.errors.sucursal_id?.message}>
                <Select id="u-suc" {...formUsuario.register('sucursal_id')}>
                  <option value="">Global (solo superadmin/contador)</option>
                  {/* Solo las activas: una sucursal desactivada no recibe usuarios (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H2) */}
                  {(sucursales.data ?? []).filter((s) => s.activa).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </Select>
              </Campo>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNuevoUsuario(false)}>
                Cancelar
              </Button>
              <Button type="submit" cargando={crearUsuario.isPending}>
                Crear usuario
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
